import { ArrowUpRight } from "lucide-react"
import type { ReactNode } from "react"

export interface NumberedStep {
  /** Zero-padded step number, e.g. "01", "02", "03" — kept as a string for typographic control */
  number: string
  /** Bold step title */
  title: string
  /** One-sentence supporting copy. Accepts ReactNode so callers can embed inline icons / tooltips. */
  description: ReactNode
}

interface NumberedStepListProps {
  /** The ordered step items to render */
  steps: NumberedStep[]
  /** Whether to show a trailing arrow on each row (default: true) */
  showArrow?: boolean
  /** Additional classes on the outer `<ol>` */
  className?: string
}

/**
 * NumberedStepList — editorial 01/02/03 ordered list with a hard black
 * border-top on the list and hard black dividers between each row.
 * Used in the "How it works" section on `/tools` and anywhere a numbered
 * progression needs a magazine-style treatment.
 *
 * Each row is a 3-column grid: [number | title+description | arrow].
 */
export function NumberedStepList({
  steps,
  showArrow = true,
  className = "",
}: NumberedStepListProps) {
  return (
    <ol className={`list-none p-0 m-0 border-t border-gray-900 ${className}`}>
      {steps.map((s) => (
        <li
          key={s.number}
          className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
        >
          <div className="font-mono text-sm text-[#ff9951] font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px]">
            {s.number}
          </div>
          <div>
            <h3 className="text-[18px] sm:text-[22px] font-extrabold tracking-[-0.02em] text-gray-900 m-0">
              {s.title}
            </h3>
            <p className="mt-1.5 text-sm text-gray-600 leading-[1.55]">{s.description}</p>
          </div>
          {showArrow ? (
            <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
          ) : (
            <span />
          )}
        </li>
      ))}
    </ol>
  )
}
